import { computed, inject, Injectable, Signal } from '@angular/core';
import { Data, Route, Router } from '@angular/router';
import { isUndefined } from 'lodash-es';

export type CumLaudeRouteData = Data & {
	/**
	 * Naam het dashboard/menu item.
	 */
	name?: string;
	/**
	 * Paginatitel van het dashboard. Mag placeholders zoals %prognose% en %subject% bevatten.
	 * Als dit veld niet is ingesteld, wordt name gebruikt als paginatitel.
	 */
	title?: string;
	/**
	 * De groep van menus waar dit dashboard/menu item bij hoort. Op dit moment core (CumLaude dashboards), beheer (CumLaude beheer schermen) of admin (CumLaude admin schermen).
	 */
	base?: string;
};

export interface RouteDef<T extends CumLaudeRouteData> {
	path: string;
	routeData?: T;
	elements?: RouteDef<T>[];
	subRoutes?: RouteDef<T>[];
}

export type CumLaudeRoute<T extends CumLaudeRouteData> = Route & {
	data?: T;
	children?: CumLaudeRoutes<T>;
};

export type CumLaudeRoutes<T extends CumLaudeRouteData> = CumLaudeRoute<T>[];

@Injectable({
	providedIn: 'root',
})
export abstract class AppRoutesService<T extends CumLaudeRouteData> {
	readonly router = inject(Router);

	abstract routeConfig: Signal<CumLaudeRoutes<T>>;

	abstract activeAuthorisedRoutes: Signal<RouteDef<T>[]>;

	readonly routes = computed(() => this.findInRoutes(this.routeConfig()) ?? []);

	private findInRoutes(sourceRoutes: CumLaudeRoutes<T>, parent?: RouteDef<T>): RouteDef<T>[] | undefined {
		const routes: RouteDef<T>[] = [];
		for (const route of sourceRoutes) {
			routes.push(...this.findInRoute(route, parent));
		}

		if (routes.length > 0) return routes;
		else return undefined;
	}

	private findInRoute(route: CumLaudeRoute<T>, parent?: RouteDef<T>) {
		const path = route.path;
		if (isUndefined(path)) return [];
		if (parent && path.length == 0) return [];

		const children = route.children;
		if (!parent && path.length == 0) {
			if (children) return this.findInRoutes(children) ?? [];

			return [];
		}

		const data = route.data;
		if (!data) return [];

		return this.buildRouteDef(route, path, data, parent, children);
	}

	private buildRouteDef(route: CumLaudeRoute<T>, path: string, data: T, parent?: RouteDef<T>, children?: CumLaudeRoute<T>[]) {
		let routeDef: RouteDef<T> = {
			path,
			routeData: data,
		};
		routeDef = {
			...routeDef,
			elements: parent ? [...parent.elements!, routeDef] : [routeDef],
		};

		let subRoutes;
		if (children) subRoutes = this.findInRoutes(children, routeDef);

		if (subRoutes && this.isRoot(route)) {
			return subRoutes;
		}

		if (!this.hasName(data)) return [];

		routeDef.subRoutes = subRoutes;
		return [routeDef];
	}

	abstract isRoot(route: CumLaudeRoute<T>): boolean;

	abstract hasName(data: T): boolean;
}

export function getUrl({ routeElements }: { routeElements: RouteDef<any>[] }) {
	return `/${routeElements.map((element) => element.path).join('/')}`;
}

export function getBreadcrumbs({ routeElements }: { routeElements: RouteDef<any>[] }) {
	return routeElements.map((element) => element.routeData!.name!).join(' > ');
}
